// Texts of the schedule screen, the event form and its validation.
// Glossary: docs/design/i18n.md (schedule, rule, window, occurrence, event).

import { defineMessages } from '@/shared/i18n'

export const messages = defineMessages({
  ru: {
    title: 'Расписание',
    site: 'Площадка',
    newEvent: 'Новое событие',
    hint:
      'Правило описывает повторяющееся или разовое событие: когда и на какие подачи оно выводит канал в эфир. «Сейчас» и «Дальше» ниже показывают не сами правила, а их конкретные окна — вхождения на календаре.',
    conflicts: 'Конфликты в расписании:',

    // Window phases.
    phaseClosed: 'Закрыто',
    phasePending: 'Запланировано',
    phaseArmed: 'Готовится',
    phaseAwaitingTake: 'Ждёт оператора',
    phaseOnAir: 'В эфире',
    phaseOverrun: 'Затянулось',
    phaseDone: 'Прошло',

    // Recurrence.
    recurrenceOnce: 'Один раз',
    recurrenceDaily: 'Ежедневно',
    recurrenceWeekly: 'По дням недели',
    daily: 'ежедневно',
    wd0: 'вс',
    wd1: 'пн',
    wd2: 'вт',
    wd3: 'ср',
    wd4: 'чт',
    wd5: 'пт',
    wd6: 'сб',
    ruleRecurring: '{days}, {time} ({zone}), {minutes}\u00a0мин',

    // Now.
    now: 'Сейчас',
    nowEmpty: 'Сейчас по расписанию ничего не идёт.',
    badgeOverrun: 'затянулось',
    badgeOverrunTitle: 'Событие держит линию после своего конца',
    badgeManual: 'ручной старт',
    badgeManualTitle: 'Ручной старт: окно готово, в эфир выводит оператор',
    takeOnAir: 'В эфир',
    closeOccurrence: 'Закрыть вхождение',
    eventSource: 'Ссылка события: {url}',
    colLine: 'Линия',
    colPlan: 'План',
    colFact: 'Фактически',
    colConflicts: 'Конфликты',
    colActions: 'Действия',
    agentLive: 'агент исполняет это окно',
    agentLiveOverrun: ' (затянулось)',
    agentNotLive: 'агент это окно не исполняет',
    lineConflict: 'линия {line}',
    lineConflictStuck: ' (не разрешится сам)',
    channelConflict: 'канал {channel}',
    closeRoute: 'Закрыть подачу',

    // Upcoming.
    next: 'Дальше',
    nextEmpty: 'На ближайшие двое суток событий нет.',
    idle: 'По расписанию сейчас ничего не идёт и на двое суток вперёд ничего не запланировано.',
    colEvent: 'Событие',
    colStart: 'Начало',
    colLines: 'Линии',
    colStartPolicy: 'Старт',
    colSource: 'Ссылка',
    auto: 'авто',

    // Rules.
    rules: 'Правила',
    colName: 'Название',
    colWhen: 'Когда',
    colRoutes: 'Подачи',
    colEnabled: 'Включено',
    noRoutes: 'нет подач',
    routesMore: 'и ещё {count}',
    enabledSwitchTitle: 'Включить или выключить правило',
    manual: 'вручную',
    yes: 'да',
    no: 'нет',
    editRule: 'Изменить',
    deleteRule: 'Удалить',
    rulesEmpty: 'Правил нет.',
    loading: 'Загрузка…',

    // Past.
    past: 'Прошло',
    colOutcome: 'Чем кончилось',

    // Confirmations.
    takeTitle: 'Вывести событие в эфир?',
    takeText: {
      one: '«{name}» пойдёт в эфир на {count} линию: {lines}.',
      few: '«{name}» пойдёт в эфир на {count} линии: {lines}.',
      many: '«{name}» пойдёт в эфир на {count} линии: {lines}.',
      other: '«{name}» пойдёт в эфир на {count} линии: {lines}.',
    },
    close: 'Закрыть',
    closeOccurrenceTitle: 'Закрыть вхождение?',
    closeOccurrenceText:
      'Все окна события «{name}» закроются одной операцией: подачи снимутся с эфира и это вхождение больше не вернётся. Следующие вхождения правила останутся.',
    closeWindowTitle: 'Закрыть окно подачи?',
    closeWindowText: 'Подача на «{line}» снимется с эфира; остальные подачи события останутся.',
    deleteTitle: 'Удалить правило?',
    deleteText: 'Будущие окна правила «{name}» исчезнут. Окно, которое уже в эфире, само не снимется.',
  },
  en: {
    title: 'Schedule',
    site: 'Site',
    newEvent: 'New event',
    hint:
      'A rule describes a recurring or one-off event: when it puts the channel on air and on which routes. “Now” and “Upcoming” below show not the rules themselves but their concrete windows — occurrences on the calendar.',
    conflicts: 'Schedule conflicts:',

    phaseClosed: 'Closed',
    phasePending: 'Scheduled',
    phaseArmed: 'Preparing',
    phaseAwaitingTake: 'Waiting for operator',
    phaseOnAir: 'On air',
    phaseOverrun: 'Overrunning',
    phaseDone: 'Done',

    recurrenceOnce: 'Once',
    recurrenceDaily: 'Daily',
    recurrenceWeekly: 'On weekdays',
    daily: 'daily',
    wd0: 'Sun',
    wd1: 'Mon',
    wd2: 'Tue',
    wd3: 'Wed',
    wd4: 'Thu',
    wd5: 'Fri',
    wd6: 'Sat',
    ruleRecurring: '{days}, {time} ({zone}), {minutes}\u00a0min',

    now: 'Now',
    nowEmpty: 'Nothing is running on the schedule right now.',
    badgeOverrun: 'running over',
    badgeOverrunTitle: 'The event holds the line past its end',
    badgeManual: 'manual start',
    badgeManualTitle: 'Manual start: the window is ready, an operator takes it on air',
    takeOnAir: 'Take on air',
    closeOccurrence: 'Close occurrence',
    eventSource: 'Event URL: {url}',
    colLine: 'Line',
    colPlan: 'Plan',
    colFact: 'Actual',
    colConflicts: 'Conflicts',
    colActions: 'Actions',
    agentLive: 'the agent is running this window',
    agentLiveOverrun: ' (running over)',
    agentNotLive: 'the agent is not running this window',
    lineConflict: 'line {line}',
    lineConflictStuck: ' (will not resolve by itself)',
    channelConflict: 'channel {channel}',
    closeRoute: 'Close route',

    next: 'Upcoming',
    nextEmpty: 'No events in the next two days.',
    idle: 'Nothing is running on the schedule now and nothing is planned for the next two days.',
    colEvent: 'Event',
    colStart: 'Start',
    colLines: 'Lines',
    colStartPolicy: 'Start',
    colSource: 'URL',
    auto: 'auto',

    rules: 'Rules',
    colName: 'Name',
    colWhen: 'When',
    colRoutes: 'Routes',
    colEnabled: 'Enabled',
    noRoutes: 'no routes',
    routesMore: 'and {count} more',
    enabledSwitchTitle: 'Turn the rule on or off',
    manual: 'manual',
    yes: 'yes',
    no: 'no',
    editRule: 'Edit',
    deleteRule: 'Delete',
    rulesEmpty: 'No rules.',
    loading: 'Loading…',

    past: 'Past',
    colOutcome: 'Outcome',

    takeTitle: 'Take the event on air?',
    takeText: {
      one: '“{name}” goes on air on {count} line: {lines}.',
      other: '“{name}” goes on air on {count} lines: {lines}.',
    },
    close: 'Close',
    closeOccurrenceTitle: 'Close the occurrence?',
    closeOccurrenceText:
      'All windows of the event “{name}” close in one operation: the routes go off air and this occurrence will not come back. Later occurrences of the rule stay.',
    closeWindowTitle: 'Close the route window?',
    closeWindowText: 'The route to “{line}” goes off air; the other routes of the event stay.',
    deleteTitle: 'Delete the rule?',
    deleteText: 'Future windows of the rule “{name}” disappear. A window already on air is not taken off by this.',
  },
})

export const formMessages = defineMessages({
  ru: {
    titleEdit: 'Правка события',
    titleNew: 'Новое событие',
    name: 'Название',
    recurrence: 'Повтор',
    routes: 'Подачи события',
    routesHint: 'Одно событие может идти сразу на несколько линий — отметьте все подачи.',
    pickChannel: 'Канал',
    pickChannelPlaceholder: 'выберите канал',
    pickLines: 'Линии',
    pickLinesHint: 'Отметьте линии, на которые пойдёт канал. Если подачи ещё нет, она создастся выключенной при сохранении.',
    pickNewRoute: 'подачу создадим',
    pickNoLines: 'У площадки канала нет линий.',
    pickChosen: 'В событии',
    pickRemove: 'убрать',
    pickNewTag: 'новая подача',
    pickExisting: 'Выбрать из готовых подач',
    pickCreateFailed: 'Не удалось создать подачу «{route}»: {error}',
    latestEnd: 'Закончится не позже {time}',
    start: 'Начало',
    end: 'Конец',
    localZone: 'местное время ({offset})',
    inUtc: 'в UTC: {time}',
    startLocal: 'Время начала',
    startLocalHint: 'ЧЧ:ММ в указанном поясе',
    durationMin: 'Длительность, мин',
    timezone: 'Часовой пояс',
    timezoneHint: 'Например Europe/Moscow',
    validFrom: 'Период с',
    validUntil: 'Период по',
    weekdays: 'Дни недели',
    startPolicy: 'Старт',
    startPolicyHint: 'Ручной старт: окно готовится, в эфир выводит оператор',
    startAuto: 'автоматически',
    startManual: 'вручную',
    endPolicy: 'Когда время вышло',
    endPolicyHint: '«держать» — событие может затянуться',
    endHold: 'держать до закрытия оператором',
    endHard: 'снять ровно в конце',
    preroll: 'Готовить заранее, с',
    prerollHint: 'За сколько секунд до начала поднять вход. Пусто — по умолчанию площадки',
    overrunLimit: 'Затягивание не дольше, мин',
    overrunLimitHint: 'Пусто — 120 минут',
    preempt: 'Вытеснять затянувшееся событие на той же линии',
    enabled: 'Правило включено',
    source: 'Ссылка события',
    sourceHint: 'Своя ссылка для этого события: пока идёт окно, URL-вход канала берёт её, потом возвращается к своей.',
    sourceEnabled: 'У события своя ссылка',
    slot: 'Слот',
    slotPlaceholder: 'выберите URL-вход',
    urlInput: 'URL-вход',
    sourceUrl: 'Ссылка',
    maxHeight: 'Максимальная высота',
    maxHeightHint: 'Пусто — как есть',
    liveFromStart: 'Тянуть с начала трансляции',
    cancel: 'Отмена',
    saving: 'Сохраняем…',
    save: 'Сохранить',
    create: 'Создать',

    // Validation.
    vRequired: 'обязательное поле',
    vMaxLen: 'не длиннее {max} символов',
    vRoutesNone: 'выберите хотя бы одну подачу',
    vRoutesTooMany: 'не больше {max} подач в одном событии',
    vRoutesSites: 'подачи разных площадок нельзя объединить в одно событие',
    vRoutesTwice: 'одна подача выбрана дважды',
    vStart: 'укажите начало',
    vEnd: 'укажите конец',
    vEndBeforeStart: 'конец должен быть позже начала',
    vEventInPast: 'событие целиком в прошлом — агент это окно не исполнит',
    vTimeOfDay: 'время в формате ЧЧ:ММ',
    vDuration: 'длительность в минутах, больше нуля',
    vDurationDay: 'не длиннее суток',
    vTimezone: 'укажите часовой пояс',
    vWeekdays: 'выберите хотя бы один день',
    vDate: 'дата в формате ГГГГ-ММ-ДД',
    vPeriod: 'конец периода раньше его начала',
    vPreroll: 'от 0 до 3600 секунд',
    vOverrun: 'от 0 до 720 минут',
    vSlot: 'выберите слот (вход канала)',
    vUrl: 'укажите ссылку на трансляцию',
    vUrlScheme: 'ссылка должна начинаться с http:// или https://',
    vMaxHeight: 'от 144 до 4320',
  },
  en: {
    titleEdit: 'Edit event',
    titleNew: 'New event',
    name: 'Name',
    recurrence: 'Repeat',
    routes: 'Event routes',
    routesHint: 'One event can go to several lines at once — tick every route.',
    pickChannel: 'Channel',
    pickChannelPlaceholder: 'choose a channel',
    pickLines: 'Lines',
    pickLinesHint: 'Tick the lines the channel goes to. A route that does not exist yet is created (off) when you save.',
    pickNewRoute: 'a route will be created',
    pickNoLines: 'The channel’s site has no lines.',
    pickChosen: 'In the event',
    pickRemove: 'remove',
    pickNewTag: 'new route',
    pickExisting: 'Pick from existing routes',
    pickCreateFailed: 'Could not create the route “{route}”: {error}',
    latestEnd: 'Ends no later than {time}',
    start: 'Start',
    end: 'End',
    localZone: 'local time ({offset})',
    inUtc: 'in UTC: {time}',
    startLocal: 'Start time',
    startLocalHint: 'HH:MM in the given time zone',
    durationMin: 'Duration, min',
    timezone: 'Time zone',
    timezoneHint: 'For example Europe/Moscow',
    validFrom: 'Valid from',
    validUntil: 'Valid until',
    weekdays: 'Days of the week',
    startPolicy: 'Start',
    startPolicyHint: 'Manual start: the window gets ready, an operator takes it on air',
    startAuto: 'automatic',
    startManual: 'manual',
    endPolicy: 'When time is up',
    endPolicyHint: '“hold” — the event may run over',
    endHold: 'hold until an operator closes it',
    endHard: 'take off exactly at the end',
    preroll: 'Prepare ahead, s',
    prerollHint: 'How many seconds before the start the input comes up. Empty — the site default',
    overrunLimit: 'Run over for at most, min',
    overrunLimitHint: 'Empty — 120 minutes',
    preempt: 'Preempt an overrunning event on the same line',
    enabled: 'Rule enabled',
    source: 'Event URL',
    sourceHint: 'A URL of its own for this event: while the window runs, the channel’s URL input takes it, then returns to its own.',
    sourceEnabled: 'The event has its own URL',
    slot: 'Slot',
    slotPlaceholder: 'choose a URL input',
    urlInput: 'URL input',
    sourceUrl: 'URL',
    maxHeight: 'Maximum height',
    maxHeightHint: 'Empty — as is',
    liveFromStart: 'Pull from the start of the stream',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    create: 'Create',

    vRequired: 'required',
    vMaxLen: 'at most {max} characters',
    vRoutesNone: 'choose at least one route',
    vRoutesTooMany: 'at most {max} routes per event',
    vRoutesSites: 'routes of different sites cannot be combined into one event',
    vRoutesTwice: 'the same route is chosen twice',
    vStart: 'set the start',
    vEnd: 'set the end',
    vEndBeforeStart: 'the end must be after the start',
    vEventInPast: 'the whole event is in the past — the agent will not run this window',
    vTimeOfDay: 'time as HH:MM',
    vDuration: 'duration in minutes, above zero',
    vDurationDay: 'at most one day',
    vTimezone: 'set the time zone',
    vWeekdays: 'choose at least one day',
    vDate: 'date as YYYY-MM-DD',
    vPeriod: 'the period ends before it starts',
    vPreroll: '0 to 3600 seconds',
    vOverrun: '0 to 720 minutes',
    vSlot: 'choose a slot (a channel input)',
    vUrl: 'set the stream URL',
    vUrlScheme: 'the URL must start with http:// or https://',
    vMaxHeight: '144 to 4320',
  },
})
